import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  AlertTitle,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  FormControl,
  FormLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Rating,
  Link,
} from '@mui/material';
import { Satellite, Visibility, Delete, ArrowForward, Star } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { CANCELLATION_CUTOFF_UI_DEFAULT, type WebsiteSettings } from '../types';
import SatelliteViewDialog from '../components/SatelliteViewDialog';
import PaymentMethodSetup from '../components/PaymentMethodSetup';
import { useProviderAgreementGate } from '../components/agreements/ProviderAgreementSigningDialog';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PaymentBreakdownTable from '../components/PaymentBreakdownTable';
import { DEFAULT_PLATFORM_FEES } from '../utils/feeCalculation';
import { buildCustomerPaymentBreakdownLines } from '../utils/paymentBreakdownLines';

// Helper function to format UTC dates correctly (extract UTC components to avoid timezone shifts)
// When dates are stored as UTC midnight (e.g., 2026-01-12T00:00:00.000Z), they represent the calendar date
// We should extract UTC components directly, not convert to local time
const formatUTCDateString = (dateInput: Date | string): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  // Extract UTC date components directly (these represent the calendar date)
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // getUTCMonth() returns 0-11, add 1 for display
  const day = date.getUTCDate();
  return `${month}/${day}/${year}`;
};

const SERVICE_REQUEST_DRAFT_KEY = 'lawnhub-service-request-draft';

type ServiceRequestFormData = {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  propertyType: string;
  services: {
    lawnMowingTrimming: boolean;
    weedControlFertilizing: boolean;
    snowRemoval: boolean;
  };
  frequencies: {
    lawnMowingTrimming: string;
    weedControlFertilizing: string;
    snowRemoval: string;
  };
  paymentSchedules: {
    lawnMowingTrimming: 'single-payment' | 'monthly';
    weedControlFertilizing: 'single-payment' | 'monthly';
    snowRemoval: 'single-payment' | 'monthly';
  };
  lawnMowingBaggingRequired?: boolean;
};

const initialFormData = (): ServiceRequestFormData => ({
  street: '',
  city: '',
  state: '',
  zipCode: '',
  propertyType: '',
  services: {
    lawnMowingTrimming: false,
    weedControlFertilizing: false,
    snowRemoval: false, // Hidden but kept for future re-enablement
  },
  frequencies: {
    lawnMowingTrimming: '',
    weedControlFertilizing: '',
    snowRemoval: '',
  },
  paymentSchedules: {
    lawnMowingTrimming: 'single-payment',
    weedControlFertilizing: 'single-payment',
    snowRemoval: 'single-payment',
  },
  lawnMowingBaggingRequired: false,
});

const ServiceRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusNewRequest = searchParams.get('new') === '1';
  const { user } = useAuth();
  const newRequestFormRef = React.useRef<HTMLDivElement | null>(null);
  
  const [formData, setFormData] = useState<ServiceRequestFormData>(() => {
    try {
      const raw = sessionStorage.getItem(SERVICE_REQUEST_DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.formData && typeof parsed.formData === 'object') {
          // Never restore frequencies - always start with "Select Frequency" placeholder
          const { frequencies: _f, ...restParsed } = parsed.formData;
          return { ...initialFormData(), ...restParsed };
        }
      }
    } catch {
      // ignore
    }
    return initialFormData();
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [satelliteDialog, setSatelliteDialog] = useState({ open: false, address: '' });
  const [addressVerified, setAddressVerified] = useState(() => {
    try {
      const raw = sessionStorage.getItem(SERVICE_REQUEST_DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.addressVerified === 'boolean') return parsed.addressVerified;
      }
    } catch {
      // ignore
    }
    return false;
  });
  const [serviceRequests, setServiceRequests] = useState<any[]>([]);
  const [loadingServiceRequests, setLoadingServiceRequests] = useState(true);
  const [bids, setBids] = useState<any[]>([]);
  const [loadingBids, setLoadingBids] = useState(true);
  const [acceptBidDialog, setAcceptBidDialog] = useState({ open: false, bid: null as any, request: null as any });
  const [seasonalContractConfirmDialog, setSeasonalContractConfirmDialog] = useState({ open: false, bid: null as any, request: null as any });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { requestSignatures, agreementDialog } = useProviderAgreementGate();
  const [acceptingBid, setAcceptingBid] = useState(false);
  const [customerAgreement, setCustomerAgreement] = useState('');
  const [propertyDocumentFiles, setPropertyDocumentFiles] = useState<File[]>([]);
  const [paymentSetupDialog, setPaymentSetupDialog] = useState({ 
    open: false, 
    bidId: '', 
    customerName: '', 
    amount: 0,
    existingCustomerId: '' // NEW: For property-specific payment setup
  });
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(null); // Track if customer has payment method
  const [bidSortOrder, setBidSortOrder] = useState<'chronological' | 'price-low' | 'price-high'>('chronological');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, request: null as any });
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationCutoffText, setCancellationCutoffText] = useState(CANCELLATION_CUTOFF_UI_DEFAULT);

  // Persist form draft so refresh or blank-page recovery doesn't lose data
  useEffect(() => {
    try {
      const draft = { formData, addressVerified };
      sessionStorage.setItem(SERVICE_REQUEST_DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // ignore quota or other storage errors
    }
  }, [formData, addressVerified]);

  useEffect(() => {
    fetchServiceRequests();
    fetchBids();
    loadCustomerAgreement();
  }, []);

  // After marketplace signup, scroll straight to the "Request New Service" form
  useEffect(() => {
    if (!focusNewRequest || loadingServiceRequests) return;
    const timer = window.setTimeout(() => {
      newRequestFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [focusNewRequest, loadingServiceRequests]);

  useEffect(() => {
    const loadCutoff = async () => {
      try {
        const res = await apiService.get<WebsiteSettings>('/website-settings');
        if (res?.data?.cancellationCutoffDescription) setCancellationCutoffText(res.data.cancellationCutoffDescription);
      } catch {
        // keep default
      }
    };
    loadCutoff();
  }, []);

  // Check for payment method when accept bid dialog opens
  useEffect(() => {
    const checkPaymentMethod = async () => {
      if (acceptBidDialog.open && acceptBidDialog.bid) {
        try {
          const paymentMethodsResponse = await apiService.get('/payments/saved-methods');
          const paymentMethods = paymentMethodsResponse.data || [];
          const hasActive = paymentMethodsResponse.success && 
            Array.isArray(paymentMethods) && 
            paymentMethods.some((method: any) => method.status === 'active');
          setHasPaymentMethod(hasActive);
          console.log('[ACCEPT-BID-DIALOG] Payment method check:', { hasActive, methods: Array.isArray(paymentMethods) ? paymentMethods.length : 0 });
        } catch (err) {
          console.error('[ACCEPT-BID-DIALOG] Error checking payment method:', err);
          setHasPaymentMethod(null); // Unknown state
        }
      } else {
        setHasPaymentMethod(null);
      }
    };
    checkPaymentMethod();
  }, [acceptBidDialog.open, acceptBidDialog.bid]);

  const fetchServiceRequests = async () => {
    try {
      const response = await apiService.get('/service-requests?userType=customer&limit=100');
      if (response.success && response.data) {
        const requestsData = (response.data as any).serviceRequests || [];
        
        // Log what we received for debugging
        console.log('[SERVICE-REQUEST-PAGE] Fetched service requests:', requestsData.length);
        requestsData.forEach((req: any, index: number) => {
          console.log(`[SERVICE-REQUEST-PAGE] Request ${index + 1}:`, {
            id: req._id,
            serviceTypeId: req.serviceType?.id,
            serviceTypeName: req.serviceType?.name,
            serviceTypeCategory: req.serviceType?.category,
            status: req.status
          });
        });
        
        setServiceRequests(requestsData);
      }
    } catch (err) {
      console.error('Failed to fetch service requests:', err);
    } finally {
      setLoadingServiceRequests(false);
    }
  };

  const fetchBids = async () => {
    try {
      const response = await apiService.get('/bids?userType=customer');
      if (response.success && response.data) {
        setBids((response.data as any).bids || []);
      }
    } catch (err) {
      console.error('Failed to fetch bids:', err);
    } finally {
      setLoadingBids(false);
    }
  };

  const loadCustomerAgreement = async () => {
    try {
      const response = await apiService.get('/legal/customer-agreement');
      if (response.success && response.data) {
        setCustomerAgreement((response.data as any).agreement);
      } else {
        setCustomerAgreement('Customer agreement could not be loaded. Please contact support.');
      }
    } catch (err) {
      console.error('Failed to load customer agreement:', err);
      setCustomerAgreement('Customer agreement could not be loaded. Please contact support.');
    }
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('services.')) {
      const serviceName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        services: {
          ...prev.services,
          [serviceName]: checked,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
      
      // Reset address verification if any address field changes
      if (['street', 'city', 'state', 'zipCode'].includes(name)) {
        setAddressVerified(false);
      }
    }
  };

  const handleAddressSelect = (fullAddress: string, addressComponents: any) => {
    setError('');
    setFormData(prev => ({
      ...prev,
      street: addressComponents.street || prev.street,
      city: addressComponents.city || prev.city,
      state: addressComponents.state || prev.state,
      zipCode: addressComponents.zipCode || prev.zipCode,
    }));
    setAddressVerified(false);
  };

  const handleFrequencyChange = (service: string, frequency: string) => {
    setFormData(prev => ({
      ...prev,
      frequencies: {
        ...prev.frequencies,
        [service]: frequency,
      },
      // Reset payment schedule to default if not seasonal-contract
      paymentSchedules: {
        ...prev.paymentSchedules,
        [service]: frequency === 'seasonal-contract' ? prev.paymentSchedules[service as keyof typeof prev.paymentSchedules] : 'single-payment',
      },
    }));
  };

  const handlePaymentScheduleChange = (service: string, paymentSchedule: 'single-payment' | 'monthly') => {
    setFormData(prev => ({
      ...prev,
      paymentSchedules: {
        ...prev.paymentSchedules,
        [service]: paymentSchedule,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Get selected services
      const selectedServices = Object.entries(formData.services)
        .filter(([_, selected]) => selected)
        .map(([service]) => service);

      if (selectedServices.length === 0) {
        setError('Please select at least one service');
        setLoading(false);
        return;
      }

      if (!formData.street || !formData.city || !formData.state || !formData.zipCode) {
        setError('Please fill in all address fields');
        setLoading(false);
        return;
      }

      if (!formData.propertyType) {
        setError('Please select a property type');
        setLoading(false);
        return;
      }

      // Validate that each selected service has a frequency
      const missingFrequencies: string[] = [];
      selectedServices.forEach(service => {
        if (!formData.frequencies[service as keyof typeof formData.frequencies]) {
          const serviceNames: { [key: string]: string } = {
            'lawnMowingTrimming': 'Lawn Mowing/Trimming',
            'weedControlFertilizing': 'Weed Control/Fertilizing',
            'snowRemoval': 'Snow Removal',
          };
          missingFrequencies.push(serviceNames[service] || service);
        }
      });

      if (missingFrequencies.length > 0) {
        setError(`Please select a frequency for: ${missingFrequencies.join(', ')}`);
        setLoading(false);
        return;
      }

      if (!addressVerified) {
        setError('Please verify your property location using the satellite view before submitting your request');
        setLoading(false);
        return;
      }

      // Check if special frequency requires explanation in description
      // Description field removed - no longer required for special frequencies

      // Map service names to proper categories
      const serviceMapping: { [key: string]: string } = {
        'lawnMowingTrimming': 'mowing',
        'weedControlFertilizing': 'fertilizing',
        'snowRemoval': 'snow_removal',
      };

      const serviceNames: { [key: string]: string } = {
        'lawnMowingTrimming': 'Lawn Mowing/Trimming',
        'weedControlFertilizing': 'Weed Control/Fertilizing',
        'snowRemoval': 'Snow Removal',
      };

      // Create separate service requests for each selected service
      console.log('[SERVICE-REQUEST] Selected services:', selectedServices);
      console.log('[SERVICE-REQUEST] Service mapping:', serviceMapping);
      console.log('[SERVICE-REQUEST] Service names:', serviceNames);
      
      const serviceRequestsToCreate = selectedServices.map(service => {
        const serviceType = {
          id: service,
          name: serviceNames[service],
          description: 'Service request',
          category: serviceMapping[service],
        };
        console.log('[SERVICE-REQUEST] Creating service request for service:', service);
        console.log('[SERVICE-REQUEST] Service type object:', JSON.stringify(serviceType, null, 2));
        console.log('[SERVICE-REQUEST] Category mapping:', service, '->', serviceMapping[service]);
        console.log('[SERVICE-REQUEST] Name mapping:', service, '->', serviceNames[service]);
        console.log('[SERVICE-REQUEST] Frequency:', formData.frequencies[service as keyof typeof formData.frequencies]);
        
        const frequency = formData.frequencies[service as keyof typeof formData.frequencies];
        const requestData: any = {
          serviceType,
          description: 'No special instructions',
          frequency,
          lotSize: 0.25,
          propertyType: formData.propertyType,
          address: {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
          },
          requestedDate: new Date(Date.now() + 60000).toISOString(), // 1 minute from now
        };
        
        // Add payment schedule if seasonal contract
        if (frequency === 'seasonal-contract') {
          requestData.paymentSchedule = formData.paymentSchedules[service as keyof typeof formData.paymentSchedules] || 'single-payment';
        }

        // Add bagging preference for lawn mowing
        if (service === 'lawnMowingTrimming' && formData.lawnMowingBaggingRequired === true) {
          requestData.baggingRequired = true;
        }
        
        console.log('[SERVICE-REQUEST] Full request data:', JSON.stringify(requestData, null, 2));
        return requestData;
      });
      
      console.log('[SERVICE-REQUEST] Total requests to create:', serviceRequestsToCreate.length);

      // Create all service requests
      const createPromises = serviceRequestsToCreate.map(requestData => 
        apiService.post('/service-requests', requestData)
      );

      const responses = await Promise.all(createPromises);
      const allSuccessful = responses.every((response: any) => response.success);

      if (allSuccessful) {
        const attachmentPropertyTypes = ['multi_family_condo', 'commercial', 'hoa_managed'];
        if (propertyDocumentFiles.length > 0 && attachmentPropertyTypes.includes(formData.propertyType)) {
          const requestIds = (responses as any[]).map((r) => r.data?._id).filter(Boolean);
          try {
            await Promise.all(
              requestIds.map((requestId: string) => {
                const fd = new FormData();
                propertyDocumentFiles.forEach((f) => fd.append('files', f));
                return apiService.post(`/service-requests/${requestId}/attachments`, fd, { timeout: 60000 });
              })
            );
          } catch (attachErr: any) {
            console.warn('[ServiceRequest] Attachment upload failed:', attachErr);
            setError(attachErr?.message || 'Service requests were created but some document uploads failed. You can add documents later from My Services.');
          }
          setPropertyDocumentFiles([]);
        }

        const successMessage = 'Thank you for submitting your service request. Providers will see your request immediately and submit bids. You will receive an SMS message when a provider places a bid for your service. No payment setup required until you accept a bid.';
        setSuccess(successMessage);
        try {
          sessionStorage.removeItem(SERVICE_REQUEST_DRAFT_KEY);
        } catch {
          // ignore
        }
        setAddressVerified(false);
        setFormData(initialFormData());
        
        // Refresh the service requests list
        fetchServiceRequests();
        
        // Navigate to My Services (customer dashboard) after a short delay so they see the message here,
        // and pass the message in state so it shows again on the dashboard
        setTimeout(() => {
          navigate('/customer', { state: { successMessage } });
        }, 2000);
      } else {
        const failedCount = responses.filter(r => !r.success).length;
        setError(`Failed to create ${failedCount} service request${failedCount > 1 ? 's' : ''}. Please try again.`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit service request');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptBid = (bid: any, request: any) => {
    // Check if bid has seasonal contracts
    const hasSeasonalContract = bid.servicePricing && bid.servicePricing.some((sp: any) => 
      sp.isSeasonalContract || sp.frequency === 'seasonal-contract'
    );

    if (hasSeasonalContract) {
      // Show seasonal contract confirmation dialog first
      setSeasonalContractConfirmDialog({ open: true, bid, request });
    } else {
      // No seasonal contract - proceed directly to accept bid dialog
      setAcceptBidDialog({ open: true, bid, request });
      setAgreedToTerms(false);
    }
  };

  const handleCancelAcceptBid = () => {
    setAcceptBidDialog({ open: false, bid: null, request: null });
    setAgreedToTerms(false);
  };

  const handleProceedToPayment = async () => {
    if (!agreedToTerms) {
      setError('You must agree to the terms and conditions to proceed');
      return;
    }

    if (!acceptBidDialog.bid || !acceptBidDialog.bid._id) {
      setError('Bid information is missing. Please try again.');
      return;
    }

    const agreementsSigned = await requestSignatures({ bidId: acceptBidDialog.bid._id });
    if (!agreementsSigned) return;

    setAcceptingBid(true);
    setError('');

    try {
      // Check if customer already has an active payment method
      const paymentMethodsResponse = await apiService.get('/payments/saved-methods');
      const hasActivePaymentMethod = paymentMethodsResponse.success && 
        Array.isArray(paymentMethodsResponse.data) && 
        paymentMethodsResponse.data.some((method: any) => method.status === 'active');

      if (hasActivePaymentMethod) {
        // Customer has a payment method - accept bid directly
        console.log('[PAYMENT-SETUP] Active payment method found, accepting bid directly:', acceptBidDialog.bid._id);
        const response = await apiService.post('/bids/accept', {
          bidId: acceptBidDialog.bid._id,
          customerAgreementAccepted: true
        });

        if (response.success) {
          setAcceptBidDialog({ open: false, bid: null, request: null });
          setAgreedToTerms(false);
          setSuccess('Bid accepted successfully!');
          // Refresh the page data
          window.location.reload();
        } else {
          setError(response.error || 'Failed to accept bid. Please try again.');
        }
      } else {
        // No payment method - show payment setup dialog
        console.log('[PAYMENT-SETUP] No active payment method found, opening payment setup dialog for bid:', acceptBidDialog.bid._id);
        setPaymentSetupDialog({
          open: true,
          bidId: acceptBidDialog.bid._id,
          customerName: `${user?.firstName} ${user?.lastName}`,
          amount: acceptBidDialog.bid.price,
          existingCustomerId: '' // Will be determined during payment setup if needed
        });
      }
    } catch (err: any) {
      console.error('[PAYMENT-SETUP] Error checking payment methods:', err);
      // If check fails, show payment setup dialog as fallback
      setPaymentSetupDialog({
        open: true,
        bidId: acceptBidDialog.bid._id,
        customerName: `${user?.firstName} ${user?.lastName}`,
        amount: acceptBidDialog.bid.price,
        existingCustomerId: ''
      });
    } finally {
      setAcceptingBid(false);
    }
  };


  const handleCancelRequest = async (request: any) => {
    const requestId = request?._id || request?.id;
    if (!requestId) {
      setError('Invalid request. Please try again.');
      return;
    }
    setCancelLoading(true);
    setError('');
    const isPendingOrBidding = request?.status === 'pending' || request?.status === 'bidding';
    const reason = isPendingOrBidding
      ? 'Customer cancelled pending request'
      : (cancellationReason.trim() || undefined);
    if (!reason) {
      setError('Please provide a reason for cancellation.');
      setCancelLoading(false);
      return;
    }
    try {
      const response = await apiService.post(`/service-requests/${requestId}/cancel`, {
        cancellationReason: reason,
      });
      
      if (response.success) {
        setDeleteDialog({ open: false, request: null });
        setCancellationReason('');
        setSuccess(response.message || 'Service request cancelled successfully!');
        if ((response as any).warnings && Array.isArray((response as any).warnings) && (response as any).warnings.length > 0) {
          setSuccess(`${response.message || 'Service request cancelled successfully!'}. ${(response as any).warnings.join(' ')}`);
        }
        fetchServiceRequests();
        fetchBids();
      } else {
        throw new Error(response.error || 'Failed to cancel service request');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to cancel service request');
    } finally {
      setCancelLoading(false);
    }
  };

  const handlePaymentSetupSuccess = async () => {
    setPaymentSetupDialog({ open: false, bidId: '', customerName: '', amount: 0, existingCustomerId: '' });
    
    // After payment setup, accept the bid
    try {
      const response = await apiService.post('/bids/accept', {
        bidId: acceptBidDialog.bid._id,
        customerAgreementAccepted: true
      });

      if (response.success) {
        // Bid accepted successfully
        setAcceptBidDialog({ open: false, bid: null, request: null });
        setAgreedToTerms(false);
        setSuccess('Payment method set up and bid accepted successfully! Redirecting to dashboard...');
        
        // Refresh data
        fetchServiceRequests();
        fetchBids();
        
        // Redirect to dashboard after short delay
        setTimeout(() => {
          navigate('/customer');
        }, 2000);
      } else {
        setError(response.error || 'Failed to accept bid after payment setup. Please try again.');
      }
    } catch (err: any) {
      // If bid is already accepted or no longer available, that's fine - redirect to dashboard
      if (err.message?.includes('no longer available') || err.message?.includes('already accepted')) {
        setAcceptBidDialog({ open: false, bid: null, request: null });
        setAgreedToTerms(false);
        setSuccess('Payment method set up successfully! Redirecting to dashboard...');
        
        fetchServiceRequests();
        fetchBids();
        
        setTimeout(() => {
          navigate('/customer');
        }, 2000);
      } else {
        setError(err.message || 'Failed to accept bid after payment setup. Please try accepting the bid again.');
      }
    }
  };

  const handlePaymentSetupClose = () => {
    setPaymentSetupDialog({ open: false, bidId: '', customerName: '', amount: 0, existingCustomerId: '' });
  };

  // Helper functions to separate service requests
  const getOpenServiceRequests = () => {
    return serviceRequests.filter(request => 
      ['pending', 'bidding'].includes(request.status)
    );
  };

  const getClosedServiceRequests = () => {
    return serviceRequests.filter(request => 
      // Show all non-open requests - accepted/scheduled/in-progress services appear in Request History
      // isImportedCustomer is for fee calculation (1% vs 5%), not for hiding - customer should see all their services
      ['accepted', 'scheduled', 'in-progress', 'declined', 'completed', 'cancelled', 'expired_unaccepted'].includes(request.status)
    );
  };

  const getBidsForRequest = (requestId: string) => {
    const filteredBids = bids.filter(bid => bid.requestId._id === requestId);
    
    // Sort bids based on the selected sort order
    if (bidSortOrder === 'chronological') {
      // Sort by creation date (oldest first)
      return filteredBids.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    } else if (bidSortOrder === 'price-low' || bidSortOrder === 'price-high') {
      // Sort by price based on the most frequent service
      const sorted = [...filteredBids].sort((a, b) => {
        const aPrice = getPriceForMostFrequentService(a);
        const bPrice = getPriceForMostFrequentService(b);
        return bidSortOrder === 'price-low' ? aPrice - bPrice : bPrice - aPrice;
      });
      return sorted;
    }
    
    return filteredBids;
  };

  const getPriceForMostFrequentService = (bid: any) => {
    if (!bid.servicePricing || bid.servicePricing.length === 0) {
      return 0;
    }

    // If only one service, return its price
    if (bid.servicePricing.length === 1) {
      return bid.servicePricing[0].price || 0;
    }

    // Find the most frequent service by comparing frequency strings
    // Weekly > Monthly > One-time
    const frequencyOrder = { 'weekly': 3, 'bi-weekly': 2, 'monthly': 2, 'per-instance': 1, 'seasonal-contract': 1 };
    
    let mostFrequent = bid.servicePricing[0];
    let maxFrequency = frequencyOrder[bid.servicePricing[0].frequency?.toLowerCase() as keyof typeof frequencyOrder] || 0;
    
    for (let i = 1; i < bid.servicePricing.length; i++) {
      const currentFreq = frequencyOrder[bid.servicePricing[i].frequency?.toLowerCase() as keyof typeof frequencyOrder] || 0;
      if (currentFreq > maxFrequency) {
        maxFrequency = currentFreq;
        mostFrequent = bid.servicePricing[i];
      }
    }
    
    return mostFrequent.price || 0;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatFrequencyLabel = (frequency: string) => {
    const frequencyMap: { [key: string]: string } = {
      'weekly': 'Weekly',
      'bi-weekly': 'Bi-Weekly',
      'monthly': 'Monthly',
      'per-instance': 'Per Instance',
      'seasonal-contract': 'Seasonal Contract'
    };
    return frequencyMap[frequency] || frequency.charAt(0).toUpperCase() + frequency.slice(1).replace(/-/g, ' ');
  };

  return (
    <Container component="main" maxWidth="md" sx={{ py: 4 }}>
      {/* Open Service Requests Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          My Service Requests
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Open requests are currently accepting bids.
        </Typography>
        {loadingServiceRequests ? (
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Loading...
              </Typography>
            </CardContent>
          </Card>
        ) : serviceRequests.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No service requests yet. Create a service request to get started!
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {/* Open Service Requests */}
            {getOpenServiceRequests().length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  Open Requests ({getOpenServiceRequests().length})
                </Typography>
                {getOpenServiceRequests().map((request: any) => (
                  <Card key={request._id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6">
                            Service Request #{request._id.slice(-6)}
                          </Typography>
                          {/* Delete button below title */}
                          {request.status !== 'completed' && request.status !== 'cancelled' && request.status !== 'pending_cancellation' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => {
                                setDeleteDialog({ open: true, request });
                                setCancellationReason(''); // Reset cancellation reason when opening dialog
                              }}
                              sx={{ mt: 1 }}
                            >
                              Cancel Service Request
                            </Button>
                          )}
                        </Box>
                        <Chip 
                          label={request.status?.charAt(0).toUpperCase() + request.status?.slice(1)} 
                          color={
                            request.status === 'pending' ? 'warning' :
                            request.status === 'bidding' ? 'info' : 'default'
                          }
                          size="small"
                        />
                      </Box>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Address:</strong> {request.address?.street}, {request.address?.city}, {request.address?.state} {request.address?.zipCode}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Services:</strong> {request.serviceType?.name || 'N/A'}
                        {(request.serviceType?.category === 'mowing' || request.serviceType?.id === 'lawnMowingTrimming') && request.baggingRequired && (
                          <Chip label="Bagging Requested" size="small" color="default" variant="outlined" sx={{ ml: 1, verticalAlign: 'middle' }} />
                        )}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Property Size:</strong> {request.propertySize} sq ft
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Requested Date:</strong> {new Date(request.requestedDate).toLocaleDateString()}
                      </Typography>
                      
                      {/* Seasonal Contract Info - Before Bid Acceptance */}
                      {request.frequency === 'seasonal-contract' && !request.acceptedBidId && (
                        <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
                          <Typography variant="body2" fontWeight="bold" color="primary.dark">
                            Seasonal Contract - {request.paymentSchedule === 'monthly' ? 'Monthly Payment' : 'One-Time Payment'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Contract details will be available after accepting a bid
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Seasonal Contract Info - After Bid Acceptance */}
                      {request.frequency === 'seasonal-contract' && request.acceptedBidId && request.acceptedBidId.servicePricing && Array.isArray(request.acceptedBidId.servicePricing) && (() => {
                        const serviceCategory = request.serviceType?.category;
                        const matchingServicePricing = request.acceptedBidId.servicePricing.find((sp: any) => {
                          if (serviceCategory === 'mowing') {
                            return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
                          } else if (serviceCategory === 'fertilizing') {
                            return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
                          } else if (serviceCategory === 'snow_removal') {
                            return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
                          }
                          return false;
                        });

                        const isSeasonalContract = matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract';
                        const seasonalDetails = matchingServicePricing?.seasonalContractDetails;
                        const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';

                        // Calculate monthly payment info if monthly
                        let monthlyPaymentInfo: { monthlyAmount: number; firstPaymentDate: Date; billingMonths: number } | null = null;
                        if (paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice && seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate) {
                          const contractStart = new Date(seasonalDetails.contractStartDate);
                          const contractEnd = new Date(seasonalDetails.contractEndDate);
                          const monthsDiff = (contractEnd.getFullYear() - contractStart.getFullYear()) * 12 +
                                            (contractEnd.getMonth() - contractStart.getMonth());
                          const billingMonths = Math.max(1, monthsDiff);
                          const monthlyAmount = seasonalDetails.totalContractPrice / billingMonths;
                          monthlyPaymentInfo = {
                            monthlyAmount,
                            firstPaymentDate: contractStart,
                            billingMonths
                          };
                        }

                        if (isSeasonalContract && seasonalDetails) {
                          return (
                            <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
                              <Typography variant="body2" fontWeight="bold" color="primary.dark" gutterBottom>
                                Seasonal Contract
                              </Typography>

                              {/* Contract Dates */}
                              {seasonalDetails.contractStartDate && seasonalDetails.contractEndDate && (
                                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                                  <strong>Contract Period:</strong> {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                                </Typography>
                              )}

                              {/* Total Contract Price */}
                              {seasonalDetails.totalContractPrice && (
                                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                                  <strong>Total Contract Price:</strong> ${seasonalDetails.totalContractPrice.toFixed(2)}
                                </Typography>
                              )}

                              {/* Payment Schedule */}
                              {paymentSchedule === 'monthly' && monthlyPaymentInfo ? (
                                <Box sx={{ mt: 1, p: 1, backgroundColor: 'white', borderRadius: 0.5 }}>
                                  <Typography variant="body2" fontWeight="medium" color="primary.dark" gutterBottom>
                                    Monthly Payment Plan
                                  </Typography>
                                  <Typography variant="body2" color="text.primary">
                                    <strong>${monthlyPaymentInfo.monthlyAmount.toFixed(2)}</strong> per month
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    First Payment: <strong>{formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}</strong>
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                                  <strong>Payment Schedule:</strong> One-Time Payment (Full payment after first service)
                                </Typography>
                              )}
                            </Box>
                          );
                        }
                        return null;
                      })()}
                      
                      {request.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          <strong>Notes:</strong> {request.description}
                        </Typography>
                      )}
                      
                      {/* Bids Section */}
                      {getBidsForRequest(request._id).length > 0 && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'grey.200' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ color: 'primary.main' }}>
                              Bids Received ({getBidsForRequest(request._id).length})
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel>Sort by</InputLabel>
                              <Select
                                value={bidSortOrder}
                                label="Sort by"
                                onChange={(e) => setBidSortOrder(e.target.value as any)}
                              >
                                <MenuItem value="chronological">Chronological</MenuItem>
                                <MenuItem value="price-low">Price: Low to High</MenuItem>
                                <MenuItem value="price-high">Price: High to Low</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          {getBidsForRequest(request._id).map((bid: any) => (
                            <Card key={bid._id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                  <Box>
                                    <Typography variant="h6" fontWeight="medium">
                                      {bid.providerId?.businessName || 'Provider'}
                                    </Typography>
                                    {bid.providerId?.rating && bid.providerId.rating > 0 ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                        <Rating
                                          value={bid.providerId.rating}
                                          readOnly
                                          precision={0.5}
                                          size="small"
                                          emptyIcon={<Star style={{ opacity: 0.55 }} fontSize="inherit" />}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                                          ({bid.providerId.rating.toFixed(1)})
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        No reviews yet
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip 
                                    label="Provider" 
                                    color="primary" 
                                    size="small"
                                  />
                                </Box>
                                {bid.description && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    <strong>Description:</strong> {bid.description}
                                  </Typography>
                                )}
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Submitted:</strong> {new Date(bid.createdAt).toLocaleDateString()}
                                </Typography>
                                
                                {/* Service Pricing Breakdown */}
                                {bid.servicePricing && bid.servicePricing.length > 0 && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Service Breakdown:
                                    </Typography>
                                    {bid.servicePricing.map((sp: any, index: number) => {
                                      // Calculate monthly payment info for seasonal contracts
                                      const isSeasonalContract = sp.isSeasonalContract || sp.frequency === 'seasonal-contract';
                                      const seasonalDetails = sp.seasonalContractDetails;
                                      const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';
                                      
                                      // Debug logging
                                      if (isSeasonalContract) {
                                        console.log('[BID-DISPLAY] Seasonal contract detected:', {
                                          serviceName: sp.serviceName,
                                          isSeasonalContract,
                                          hasSeasonalDetails: !!seasonalDetails,
                                          paymentSchedule,
                                          seasonalDetails: seasonalDetails ? {
                                            totalContractPrice: seasonalDetails.totalContractPrice,
                                            contractStartDate: seasonalDetails.contractStartDate,
                                            contractEndDate: seasonalDetails.contractEndDate,
                                            paymentSchedule: seasonalDetails.paymentSchedule
                                          } : null
                                        });
                                      }
                                      
                                      let monthlyPaymentInfo: { monthlyAmount: number; firstPaymentDate: Date } | null = null;
                                      if (isSeasonalContract && paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice && seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate) {
                                        try {
                                          const start = new Date(seasonalDetails.contractStartDate);
                                          const end = new Date(seasonalDetails.contractEndDate);
                                          // Use UTC date component for comparison (matches backend logic)
                                          const startsOnFirst = start.getUTCDate() === 1;
                                          
                                          let billingMonths: number;
                                          if (startsOnFirst) {
                                            // Use UTC components for calculation (matches backend logic)
                                            billingMonths = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + 
                                                           (end.getUTCMonth() - start.getUTCMonth()) + 1;
                                          } else {
                                            // Use UTC components for next month calculation
                                            const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                                            const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                                            billingMonths = (end.getUTCFullYear() - nextMonthYear) * 12 + 
                                                           (end.getUTCMonth() - nextMonthMonth) + 1;
                                          }
                                          
                                          if (billingMonths > 0) {
                                            const monthlyAmount = Math.round((seasonalDetails.totalContractPrice / billingMonths) * 100) / 100;
                                            // Create first payment date using UTC components (matches backend billing_cycle_anchor logic)
                                            let firstPaymentDate: Date;
                                            if (startsOnFirst) {
                                              firstPaymentDate = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
                                            } else {
                                              const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                                              const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                                              firstPaymentDate = new Date(Date.UTC(nextMonthYear, nextMonthMonth, 1));
                                            }
                                            monthlyPaymentInfo = { monthlyAmount, firstPaymentDate };
                                          }
                                        } catch (e) {
                                          console.error('[BID-DISPLAY] Error calculating monthly payment:', e);
                                        }
                                      }
                                      
                                      return (
                                        <Box key={index} sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, border: 1, borderColor: 'grey.300', mb: 1 }}>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                              <Typography variant="body2" fontWeight="medium">
                                                {sp.serviceName}
                                              </Typography>
                                              {(request.serviceType?.category === 'mowing' || request.serviceType?.id === 'lawnMowingTrimming' || sp.service === 'lawnMowingTrimming' || (sp.serviceName && String(sp.serviceName).toLowerCase().includes('mowing'))) && request.baggingRequired && (
                                                <Chip label="Bagging" size="small" color="default" variant="outlined" />
                                              )}
                                            </Box>
                                            <Typography variant="body2" color="primary" fontWeight="bold">
                                              {isSeasonalContract && seasonalDetails?.totalContractPrice ? 
                                                formatCurrency(seasonalDetails.totalContractPrice) : 
                                                formatCurrency(sp.price)}
                                            </Typography>
                                          </Box>
                                          {isSeasonalContract && seasonalDetails?.totalContractPrice ? (
                                            <>
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Seasonal Contract - Service Frequency: <strong>{formatFrequencyLabel(seasonalDetails.serviceFrequency || 'weekly')}</strong>
                                              </Typography>
                                              {monthlyPaymentInfo && (
                                                <Typography variant="caption" color="primary.dark" display="block" sx={{ mt: 0.5, fontWeight: 'medium' }}>
                                                  Monthly: {formatCurrency(monthlyPaymentInfo.monthlyAmount)} • First: {formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}
                                                </Typography>
                                              )}
                                              {!monthlyPaymentInfo && seasonalDetails.contractStartDate && seasonalDetails.contractEndDate && (
                                                <>
                                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                    Contract: {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontWeight: 'medium' }}>
                                                    Payment Schedule: <strong>One-Time Payment</strong>
                                                  </Typography>
                                                </>
                                              )}
                                            </>
                                          ) : (
                                            <Typography variant="caption" color="text.secondary">
                                              {formatFrequencyLabel(sp.frequency || 'weekly')}
                                            </Typography>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}
                                
                                {/* Accept Bid Button */}
                                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'grey.300' }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    onClick={() => handleAcceptBid(bid, request)}
                                  >
                                    Accept {bid.providerId?.businessName || 'Provider'}'s Bid
                                  </Button>
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Grid>
            )}

            {/* No requests message */}
            {getOpenServiceRequests().length === 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No service requests found. Create a service request to get started!
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      {/* New Service Request Form - Now below the existing requests */}
      <Paper elevation={3} sx={{ p: 4 }} ref={newRequestFormRef}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography component="h1" variant="h4" gutterBottom>
            Request New Service
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tell us about your lawn care needs and we'll connect you with local providers
          </Typography>
        </Box>

        <Box
          sx={{
            mb: 3,
            p: 2.5,
            bgcolor: 'grey.100',
            borderRadius: 2,
            border: '2px solid',
            borderColor: 'primary.main',
            boxShadow: 1,
            textAlign: 'center',
          }}
        >
          <Typography variant="subtitle1" color="primary.dark" gutterBottom fontWeight="700" sx={{ fontSize: '1.65rem' }}>
            How it works
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
            <Typography component="span" variant="body1" color="primary.dark" fontWeight="600" sx={{ fontSize: '1.25rem' }}>
              1. Submit request
            </Typography>
            <ArrowForward sx={{ color: 'primary.main', fontSize: 32, mx: 0.5, verticalAlign: 'middle' }} />
            <Typography component="span" variant="body1" color="primary.dark" fontWeight="600" sx={{ fontSize: '1.25rem' }}>
              2. Get bids
            </Typography>
            <ArrowForward sx={{ color: 'primary.main', fontSize: 32, mx: 0.5, verticalAlign: 'middle' }} />
            <Typography component="span" variant="body1" color="primary.dark" fontWeight="600" sx={{ fontSize: '1.25rem' }}>
              3. Accept a bid
            </Typography>
            <ArrowForward sx={{ color: 'primary.main', fontSize: 32, mx: 0.5, verticalAlign: 'middle' }} />
            <Typography component="span" variant="body1" color="primary.dark" fontWeight="600" sx={{ fontSize: '1.25rem' }}>
              4. Set up payment
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success.includes('No payment setup required until you accept a bid.') ? (
              <>
                {success.replace('No payment setup required until you accept a bid.', '').trim()}{' '}
                <strong>No payment setup required until you accept a bid.</strong>
              </>
            ) : (
              success
            )}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Property Address */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Property Address
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <AddressAutocomplete
                value={formData.street}
                onChange={handleAddressSelect}
                label="Street Address"
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                required
                fullWidth
                label="State"
                name="state"
                value={formData.state}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                required
                fullWidth
                label="ZIP Code"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            {/* Address Verification */}
            {formData.street && formData.city && formData.state && formData.zipCode && (
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 2, 
                  border: '1px solid', 
                  borderColor: addressVerified ? 'success.main' : 'warning.main',
                  borderRadius: 1,
                  backgroundColor: addressVerified ? 'success.50' : 'warning.50'
                }}>
                  <Typography variant="h6" gutterBottom>
                    🛰️ Verify Property Location
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please verify that the satellite image shows your actual property before submitting your request.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Satellite />}
                      onClick={() => {
                        const fullAddress = `${formData.street}, ${formData.city}, ${formData.state} ${formData.zipCode}`;
                        setSatelliteDialog({ open: true, address: fullAddress });
                      }}
                      disabled={loading}
                    >
                      View Satellite Image
                    </Button>
                    
                    {addressVerified ? (
                      <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        ✅ Address Verified
                      </Typography>
                    ) : (
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => setAddressVerified(true)}
                        disabled={loading}
                        size="small"
                      >
                        Confirm This is Correct
                      </Button>
                    )}
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary">
                    Click the satellite view button to see an aerial view of your property. Make sure the location matches your actual address.
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Property Type */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Property Type
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Please select the type of property for this service request
              </Typography>
            </Grid>

            <Grid item xs={12} sm={8}>
              <FormControl fullWidth required>
                <InputLabel>Property Type</InputLabel>
                <Select
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange as any}
                  label="Property Type"
                  disabled={loading}
                >
                  <MenuItem value="single_residential">Single Family Residential</MenuItem>
                  <MenuItem value="multi_family_condo">Multi-Family / Condo</MenuItem>
                  <MenuItem value="commercial">Commercial</MenuItem>
                  <MenuItem value="vacant_lot">Vacant Lot</MenuItem>
                  <MenuItem value="hoa_managed">HOA-Managed Property</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Property documents (optional) for Multi-Family, Commercial, HOA */}
            {['multi_family_condo', 'commercial', 'hoa_managed'].includes(formData.propertyType) && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Property documents (optional)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Upload plot surveys, property photos, or other documents that help providers prepare accurate bids (images or PDF, max 10 files, 10MB each).
                  </Typography>
                  <input
                    accept="image/*,.pdf,application/pdf"
                    type="file"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setPropertyDocumentFiles(prev => {
                        const combined = [...prev, ...files];
                        return combined.slice(-10);
                      });
                      e.target.value = '';
                    }}
                    style={{ display: 'block', marginBottom: 8 }}
                  />
                  {propertyDocumentFiles.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {propertyDocumentFiles.map((f, i) => (
                        <Typography key={i} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {f.name}
                          <Button
                            size="small"
                            color="secondary"
                            onClick={() => setPropertyDocumentFiles(prev => prev.filter((_, j) => j !== i))}
                          >
                            Remove
                          </Button>
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}

            {/* Services - Each service has its own section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Services Needed
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Select all services you need and configure each one
              </Typography>
            </Grid>

            {/* Lawn Mowing Service Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="services.lawnMowingTrimming"
                      checked={formData.services.lawnMowingTrimming}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" fontWeight="medium">
                      Lawn Mowing/Trimming
                    </Typography>
                  }
                  sx={{ mb: formData.services.lawnMowingTrimming ? 2 : 0 }}
                />
                {formData.services.lawnMowingTrimming && (
                  <Box sx={{ ml: 4, mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            Require grass clippings to be bagged?
                          </FormLabel>
                          <RadioGroup
                            row
                            value={formData.lawnMowingBaggingRequired === true ? 'yes' : 'no'}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              lawnMowingBaggingRequired: e.target.value === 'yes',
                            }))}
                          >
                            <FormControlLabel value="no" control={<Radio size="small" />} label="No" disabled={loading} />
                            <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" disabled={loading} />
                          </RadioGroup>
                          {formData.lawnMowingBaggingRequired && (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                              Requesting grass clippings to be bagged may increase the price for the service.
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                          <InputLabel id="lawn-freq-label" shrink required={false}>Service Frequency</InputLabel>
                          <Select
                            labelId="lawn-freq-label"
                            value={formData.frequencies.lawnMowingTrimming}
                            onChange={(e) => handleFrequencyChange('lawnMowingTrimming', e.target.value)}
                            label="Service Frequency"
                            disabled={loading}
                            displayEmpty
                            renderValue={(v) => v ? formatFrequencyLabel(v) : 'Select Frequency'}
                          >
                            <MenuItem value="">Select Frequency</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="bi-weekly">Bi-Weekly (Every 2 weeks)</MenuItem>
                            <MenuItem value="seasonal-contract">Seasonal Contract</MenuItem>
                            <MenuItem value="per-instance">Per Instance</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {formData.frequencies.lawnMowingTrimming === 'seasonal-contract' && (
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Payment Schedule</InputLabel>
                            <Select
                              value={formData.paymentSchedules.lawnMowingTrimming}
                              onChange={(e) => handlePaymentScheduleChange('lawnMowingTrimming', e.target.value as 'single-payment' | 'monthly')}
                              label="Payment Schedule"
                              disabled={loading}
                            >
                              <MenuItem value="single-payment">One-Time Payment (Full payment after first service)</MenuItem>
                              <MenuItem value="monthly">Monthly Payments (Charged on 1st of each month)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Weed Control Service Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="services.weedControlFertilizing"
                      checked={formData.services.weedControlFertilizing}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" fontWeight="medium">
                      Weed Control/Fertilizing
                    </Typography>
                  }
                  sx={{ mb: formData.services.weedControlFertilizing ? 2 : 0 }}
                />
                {formData.services.weedControlFertilizing && (
                  <Box sx={{ ml: 4, mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                          <InputLabel id="weed-freq-label" shrink required={false}>Service Frequency</InputLabel>
                          <Select
                            labelId="weed-freq-label"
                            value={formData.frequencies.weedControlFertilizing}
                            onChange={(e) => handleFrequencyChange('weedControlFertilizing', e.target.value)}
                            label="Service Frequency"
                            disabled={loading}
                            displayEmpty
                            renderValue={(v) => v ? formatFrequencyLabel(v) : 'Select Frequency'}
                          >
                            <MenuItem value="">Select Frequency</MenuItem>
                            <MenuItem value="bi-weekly">Bi-Weekly (Every 2 weeks)</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="seasonal-contract">Seasonal Contract</MenuItem>
                            <MenuItem value="per-instance">Per Instance</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {formData.frequencies.weedControlFertilizing === 'seasonal-contract' && (
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Payment Schedule</InputLabel>
                            <Select
                              value={formData.paymentSchedules.weedControlFertilizing}
                              onChange={(e) => handlePaymentScheduleChange('weedControlFertilizing', e.target.value as 'single-payment' | 'monthly')}
                              label="Payment Schedule"
                              disabled={loading}
                            >
                              <MenuItem value="single-payment">One-Time Payment (Full payment after first service)</MenuItem>
                              <MenuItem value="monthly">Monthly Payments (Charged on 1st of each month)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Snow Removal Service Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="services.snowRemoval"
                      checked={formData.services.snowRemoval}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  }
                  label={
                    <Typography variant="subtitle1" fontWeight="medium">
                      Snow Removal
                    </Typography>
                  }
                  sx={{ mb: formData.services.snowRemoval ? 2 : 0 }}
                />
                {formData.services.snowRemoval && (
                  <Box sx={{ ml: 4, mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required>
                          <InputLabel id="snow-freq-label" shrink required={false}>Service Frequency</InputLabel>
                          <Select
                            labelId="snow-freq-label"
                            value={formData.frequencies.snowRemoval}
                            onChange={(e) => handleFrequencyChange('snowRemoval', e.target.value)}
                            label="Service Frequency"
                            disabled={loading}
                            displayEmpty
                            renderValue={(v) => v ? formatFrequencyLabel(v) : 'Select Frequency'}
                          >
                            <MenuItem value="">Select Frequency</MenuItem>
                            <MenuItem value="seasonal-contract">Seasonal Contract</MenuItem>
                            <MenuItem value="per-instance">Per Instance</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {formData.frequencies.snowRemoval === 'seasonal-contract' && (
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Payment Schedule</InputLabel>
                            <Select
                              value={formData.paymentSchedules.snowRemoval}
                              onChange={(e) => handlePaymentScheduleChange('snowRemoval', e.target.value as 'single-payment' | 'monthly')}
                              label="Payment Schedule"
                              disabled={loading}
                            >
                              <MenuItem value="single-payment">One-Time Payment (Full payment after first service)</MenuItem>
                              <MenuItem value="monthly">Monthly Payments (Charged on 1st of each month)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>


            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/customer')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ px: 4 }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Request History Section - Below the New Request Form */}
      {getClosedServiceRequests().length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
            Request History ({getClosedServiceRequests().length})
          </Typography>
          {getClosedServiceRequests().map((request: any) => (
            <Card key={request._id} sx={{ mb: 2, bgcolor: 'grey.50' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Typography variant="h6">
                    Service Request #{request._id.slice(-6)}
                  </Typography>
                  <Chip 
                    label={
                      request.status === 'expired_unaccepted' ? 'Expired' :
                      request.status ? request.status.charAt(0).toUpperCase() + request.status.slice(1).replace(/_/g, ' ') : 'N/A'
                    }
                    color={
                      ['accepted', 'scheduled', 'in-progress', 'completed'].includes(request.status) ? 'success' :
                      request.status === 'declined' ? 'error' :
                      request.status === 'expired_unaccepted' ? 'warning' :
                      request.status === 'cancelled' ? 'default' : 'default'
                    }
                    size="small"
                  />
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Address:</strong> {request.address?.street}, {request.address?.city}, {request.address?.state} {request.address?.zipCode}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Services:</strong> {request.serviceType?.name || 'N/A'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Property Size:</strong> {request.propertySize} sq ft
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Requested Date:</strong> {new Date(request.requestedDate).toLocaleDateString()}
                </Typography>
                
                {/* Seasonal Contract Info - Before Bid Acceptance */}
                {request.frequency === 'seasonal-contract' && !request.acceptedBidId && (
                  <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
                    <Typography variant="body2" fontWeight="bold" color="primary.dark">
                      Seasonal Contract - {request.paymentSchedule === 'monthly' ? 'Monthly Payment' : 'One-Time Payment'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Contract details will be available after accepting a bid
                    </Typography>
                  </Box>
                )}
                
                {/* Seasonal Contract Info - After Bid Acceptance */}
                {request.frequency === 'seasonal-contract' && request.acceptedBidId && request.acceptedBidId.servicePricing && Array.isArray(request.acceptedBidId.servicePricing) && (() => {
                  const serviceCategory = request.serviceType?.category;
                  const matchingServicePricing = request.acceptedBidId.servicePricing.find((sp: any) => {
                    if (serviceCategory === 'mowing') {
                      return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
                    } else if (serviceCategory === 'fertilizing') {
                      return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
                    } else if (serviceCategory === 'snow_removal') {
                      return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
                    }
                    return false;
                  });

                  const isSeasonalContract = matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract';
                  const seasonalDetails = matchingServicePricing?.seasonalContractDetails;
                  const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';

                  // Calculate monthly payment info if monthly
                  let monthlyPaymentInfo: { monthlyAmount: number; firstPaymentDate: Date; billingMonths: number } | null = null;
                  if (paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice && seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate) {
                    const contractStart = new Date(seasonalDetails.contractStartDate);
                    const contractEnd = new Date(seasonalDetails.contractEndDate);
                    const monthsDiff = (contractEnd.getFullYear() - contractStart.getFullYear()) * 12 +
                                      (contractEnd.getMonth() - contractStart.getMonth());
                    const billingMonths = Math.max(1, monthsDiff);
                    const monthlyAmount = seasonalDetails.totalContractPrice / billingMonths;
                    monthlyPaymentInfo = {
                      monthlyAmount,
                      firstPaymentDate: contractStart,
                      billingMonths
                    };
                  }

                  if (isSeasonalContract && seasonalDetails) {
                    return (
                      <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
                        <Typography variant="body2" fontWeight="bold" color="primary.dark" gutterBottom>
                          Seasonal Contract
                        </Typography>

                        {/* Contract Dates */}
                        {seasonalDetails.contractStartDate && seasonalDetails.contractEndDate && (
                          <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                            <strong>Contract Period:</strong> {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                          </Typography>
                        )}

                        {/* Total Contract Price */}
                        {seasonalDetails.totalContractPrice && (
                          <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                            <strong>Total Contract Price:</strong> ${seasonalDetails.totalContractPrice.toFixed(2)}
                          </Typography>
                        )}

                        {/* Payment Schedule */}
                        {paymentSchedule === 'monthly' && monthlyPaymentInfo ? (
                          <Box sx={{ mt: 1, p: 1, backgroundColor: 'white', borderRadius: 0.5 }}>
                            <Typography variant="body2" fontWeight="medium" color="primary.dark" gutterBottom>
                              Monthly Payment Plan
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              <strong>${monthlyPaymentInfo.monthlyAmount.toFixed(2)}</strong> per month
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              First Payment: <strong>{formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}</strong>
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                            <strong>Payment Schedule:</strong> One-Time Payment (Full payment after first service)
                          </Typography>
                        )}
                      </Box>
                    );
                  }
                  return null;
                })()}
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Status:</strong> {request.status === 'accepted' ? 'Bid accepted' : 
                                         request.status === 'completed' ? 'Service completed' :
                                         request.status === 'declined' ? 'Bid declined' :
                                         request.status === 'cancelled' ? 'Request cancelled' : 'Unknown'}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Accept Bid Dialog */}
      <Dialog
        open={acceptBidDialog.open}
        onClose={handleCancelAcceptBid}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Accept Bid</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to accept a bid from <strong>{acceptBidDialog.bid?.providerId?.businessName || 'Provider'}</strong>.
          </DialogContentText>
          
          <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              Service Details
            </Typography>
            {acceptBidDialog.bid?.servicePricing && acceptBidDialog.bid.servicePricing.map((sp: any, index: number) => {
              // Calculate monthly payment info for seasonal contracts (matches BidComparisonTable logic)
              const isSeasonalContract = sp.isSeasonalContract || sp.frequency === 'seasonal-contract';
              const seasonalDetails = sp.seasonalContractDetails;
              const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';
              const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              
              let monthlyPaymentInfo: { monthlyAmount: number; firstPaymentDate: Date; totalContractPrice: number; contractStartDate: Date; contractEndDate: Date } | null = null;
              if (isSeasonalContract && paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice && seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate) {
                try {
                  const start = new Date(seasonalDetails.contractStartDate);
                  const end = new Date(seasonalDetails.contractEndDate);
                  const startsOnFirst = start.getUTCDate() === 1;
                  
                  let billingMonths: number;
                  if (startsOnFirst) {
                    billingMonths = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + 
                                   (end.getUTCMonth() - start.getUTCMonth()) + 1;
                  } else {
                    const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                    const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                    billingMonths = (end.getUTCFullYear() - nextMonthYear) * 12 + 
                                   (end.getUTCMonth() - nextMonthMonth) + 1;
                  }
                  
                  if (billingMonths > 0) {
                    const monthlyAmount = Math.round((seasonalDetails.totalContractPrice / billingMonths) * 100) / 100;
                    let firstPaymentDate: Date;
                    if (startsOnFirst) {
                      firstPaymentDate = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
                    } else {
                      const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                      const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                      firstPaymentDate = new Date(Date.UTC(nextMonthYear, nextMonthMonth, 1));
                    }
                    monthlyPaymentInfo = {
                      monthlyAmount,
                      firstPaymentDate,
                      totalContractPrice: seasonalDetails.totalContractPrice,
                      contractStartDate: start,
                      contractEndDate: end,
                    };
                  }
                } catch (e) {
                  console.error('[ACCEPT-BID-DIALOG] Error calculating monthly payment:', e);
                }
              }
              
              return (
                <Box key={index} sx={{ mb: 2, p: 2, backgroundColor: 'white', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                  {/* Service Name, Duration, and Price */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {sp.serviceName}
                      </Typography>
                      {sp.duration && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Estimated Duration: {sp.duration} {sp.duration === 1 ? 'hour' : 'hours'}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {isSeasonalContract && seasonalDetails?.totalContractPrice
                        ? formatCurrency(seasonalDetails.totalContractPrice)
                        : formatCurrency(sp.price)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  {/* Frequency Details */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" fontWeight="medium" color="text.secondary" gutterBottom>
                      Service Frequency:
                    </Typography>
                    <Typography variant="body2" sx={{ pl: 1 }}>
                      {isSeasonalContract && seasonalDetails?.serviceFrequency
                        ? formatFrequencyLabel(seasonalDetails.serviceFrequency)
                        : formatFrequencyLabel(sp.frequency)}
                    </Typography>
                    {sp.recurringDayOfWeek !== undefined && (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 1, mt: 0.5 }}>
                        Scheduled for: {dayNames[sp.recurringDayOfWeek]}s
                      </Typography>
                    )}
                    {sp.recurringDayOfMonth !== undefined && (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 1, mt: 0.5 }}>
                        Scheduled for: Day {sp.recurringDayOfMonth} of each month
                      </Typography>
                    )}
                    {sp.biWeeklyStartDate && (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 1, mt: 0.5 }}>
                        Starting: {formatUTCDateString(sp.biWeeklyStartDate)}
                      </Typography>
                    )}
                  </Box>

                  {/* Payment Details */}
                  <Box>
                    <Typography variant="body2" fontWeight="medium" color="text.secondary" gutterBottom>
                      Payment Details:
                    </Typography>
                    {isSeasonalContract ? (
                      <Box sx={{ pl: 1 }}>
                        {monthlyPaymentInfo ? (
                          <>
                            <Typography variant="body2">
                              Payment Schedule: <strong>Monthly Payments</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Monthly Amount: {formatCurrency(monthlyPaymentInfo.monthlyAmount)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              First payment on the 1st of each month during contract period
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Contract Period: {formatUTCDateString(monthlyPaymentInfo.contractStartDate)} - {formatUTCDateString(monthlyPaymentInfo.contractEndDate)}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="body2">
                              Payment Schedule: <strong>One-Time Payment</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Full payment processed after first service
                            </Typography>
                            {seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Contract Period: {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ pl: 1 }}>
                        Price per Service: <strong>{formatCurrency(sp.price)}</strong>
                      </Typography>
                    )}
                  </Box>

                  {/* Seasonal Contract Badge */}
                  {isSeasonalContract && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'grey.200' }}>
                      <Chip
                        label="Seasonal Contract"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Paper>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            What you&apos;ll be charged
          </Typography>
          <PaymentBreakdownTable
            lines={buildCustomerPaymentBreakdownLines({
              servicePricing: acceptBidDialog.bid?.servicePricing,
              fallbackPrice: acceptBidDialog.bid?.price,
              platformFeePercent:
                acceptBidDialog.bid?.platformFeePercentage ?? DEFAULT_PLATFORM_FEES.standardFeePercentage,
              salesTaxRates: acceptBidDialog.bid?.providerId?.salesTaxRates,
              address: acceptBidDialog.request?.address,
              propertyType: acceptBidDialog.request?.propertyType,
            })}
          />

          {/* Note about provider agreements - only shown for bid acceptance (new customers) */}
          <Alert severity="info" sx={{ mb: 2, py: 1, '& .MuiAlert-message': { width: '100%' } }}>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block', color: 'info.dark' }}>
              <strong>Note:</strong> The provider may have additional agreements that will need to be signed when they arrive for the first service. 
              Any agreement between you and the provider is separate and independent from the LawnHub platform and does not affect this service agreement.
            </Typography>
          </Alert>

          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, maxHeight: '200px', overflow: 'auto' }}>
            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
              {customerAgreement}
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
            }
            label={
              <>
                I have read and agree to the Customer Service Agreement and{' '}
                <Link href="/privacy-policy" target="_blank" rel="noopener">
                  Privacy Policy
                </Link>
              </>
            }
          />
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAcceptBid} disabled={acceptingBid}>
            Cancel
          </Button>
          <Button
            onClick={handleProceedToPayment}
            variant="contained"
            disabled={!agreedToTerms || acceptingBid}
          >
            {acceptingBid 
              ? 'Processing...' 
              : hasPaymentMethod === true 
                ? 'Accept Bid & Continue' 
                : hasPaymentMethod === false 
                  ? 'Set Up Payment Method' 
                  : 'Accept Bid & Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seasonal Contract Confirmation Dialog */}
      <Dialog
        open={seasonalContractConfirmDialog.open}
        onClose={() => setSeasonalContractConfirmDialog({ open: false, bid: null, request: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Confirm Seasonal Contract Terms</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Important: Please Review Contract Terms</AlertTitle>
            <Typography variant="body2">
              You are about to accept a <strong>seasonal contract</strong>. Please carefully review the payment schedule and contract terms below before proceeding.
            </Typography>
          </Alert>

          {seasonalContractConfirmDialog.bid?.servicePricing && seasonalContractConfirmDialog.bid.servicePricing
            .filter((sp: any) => sp.isSeasonalContract || sp.frequency === 'seasonal-contract')
            .map((sp: any, idx: number) => {
              const seasonalDetails = sp.seasonalContractDetails;
              const paymentSchedule = seasonalDetails?.paymentSchedule || 'single-payment';
              
              // Calculate monthly payment info if monthly
              let monthlyPaymentInfo: { monthlyAmount: number; firstPaymentDate: Date; billingMonths: number } | null = null;
              if (paymentSchedule === 'monthly' && seasonalDetails?.totalContractPrice && seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate) {
                try {
                  const start = new Date(seasonalDetails.contractStartDate);
                  const end = new Date(seasonalDetails.contractEndDate);
                  const startsOnFirst = start.getUTCDate() === 1;
                  
                  let billingMonths: number;
                  if (startsOnFirst) {
                    billingMonths = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + 
                                 (end.getUTCMonth() - start.getUTCMonth()) + 1;
                  } else {
                    const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                    const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                    billingMonths = (end.getUTCFullYear() - nextMonthYear) * 12 + 
                                   (end.getUTCMonth() - nextMonthMonth) + 1;
                  }
                  
                  if (billingMonths > 0) {
                    const monthlyAmount = Math.round((seasonalDetails.totalContractPrice / billingMonths) * 100) / 100;
                    let firstPaymentDate: Date;
                    if (startsOnFirst) {
                      firstPaymentDate = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
                    } else {
                      const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                      const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                      firstPaymentDate = new Date(Date.UTC(nextMonthYear, nextMonthMonth, 1));
                    }
                    monthlyPaymentInfo = { monthlyAmount, firstPaymentDate, billingMonths };
                  }
                } catch (e) {
                  console.error('[SEASONAL-CONFIRM] Error calculating monthly payment:', e);
                }
              }

              return (
                <Box key={idx} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '2px solid', borderColor: 'primary.main' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    {sp.serviceName}
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Total Contract Price:</strong> {formatCurrency(seasonalDetails?.totalContractPrice || 0)}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Service Frequency:</strong> {seasonalDetails?.serviceFrequency || 'weekly'}
                    </Typography>
                    
                    {seasonalDetails?.contractStartDate && seasonalDetails?.contractEndDate && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Contract Period:</strong> {formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}
                      </Typography>
                    )}

                    <Box sx={{ mt: 2, p: 2, bgcolor: paymentSchedule === 'monthly' ? 'primary.light' : 'success.light', borderRadius: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Payment Schedule: {paymentSchedule === 'monthly' ? 'Monthly Payments' : 'One-Time Payment (Full payment after first service)'}
                      </Typography>
                      
                      {paymentSchedule === 'monthly' && monthlyPaymentInfo ? (
                        <>
                          <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                            Monthly Amount: <strong>{formatCurrency(monthlyPaymentInfo.monthlyAmount)}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            First Payment Date: <strong>{formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Total Payments: <strong>{monthlyPaymentInfo.billingMonths} monthly payments</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Payments will be automatically charged on the <strong>1st of each month</strong> during the contract period.
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Payment Amount: <strong>{formatCurrency(seasonalDetails?.totalContractPrice || 0)}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Full payment will be processed after the <strong>first service is completed</strong>.
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>By proceeding, you agree to:</strong>
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>The payment schedule shown above</li>
              <li>The contract period and service frequency</li>
              <li>All terms of the seasonal contract</li>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
              If the payment schedule is incorrect, please cancel and ask the provider to submit a new bid with the correct terms.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSeasonalContractConfirmDialog({ open: false, bid: null, request: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              // Close confirmation dialog and proceed to accept bid dialog
              setSeasonalContractConfirmDialog({ open: false, bid: null, request: null });
              setAcceptBidDialog({ open: true, bid: seasonalContractConfirmDialog.bid, request: seasonalContractConfirmDialog.request });
              setAgreedToTerms(false);
            }}
            variant="contained"
            color="primary"
          >
            I Confirm - Proceed to Accept Bid
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Method Setup Dialog */}
      <PaymentMethodSetup
        open={paymentSetupDialog.open}
        onClose={handlePaymentSetupClose}
        onSuccess={handlePaymentSetupSuccess}
        bidId={paymentSetupDialog.bidId}
        customerName={paymentSetupDialog.customerName}
        amount={paymentSetupDialog.amount}
        existingCustomerId={paymentSetupDialog.existingCustomerId} // NEW: Pass property-specific ID
      />

      {/* Satellite View Dialog */}
      <SatelliteViewDialog
        open={satelliteDialog.open}
        onClose={() => setSatelliteDialog({ open: false, address: '' })}
        address={satelliteDialog.address}
        title="Verify Your Property Location"
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, request: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Service Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this service request?
            {deleteDialog.request && (deleteDialog.request.status === 'pending' || deleteDialog.request.status === 'bidding') ? (
              <>
                {' '}Cancelling will remove any bids that have been placed for this request. If you submit a new request later, providers would need to submit new bids—previous bids will not be restored.
              </>
            ) : (
              <> Your provider will be notified immediately.</>
            )}
          </DialogContentText>
          {/* For accepted/scheduled services only: show "What happens" and reason field */}
          {deleteDialog.request && deleteDialog.request.status !== 'pending' && deleteDialog.request.status !== 'bidding' && (
            <>
              <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                <AlertTitle>What happens when you cancel:</AlertTitle>
                <Typography variant="body2" component="div">
                  • All providers who have submitted bids will be notified<br/>
                  • All bids will be cancelled<br/>
                  • Service records will be preserved for billing and legal purposes<br/>
                  • If you cancel after {cancellationCutoffText}, the upcoming service may still be chargeable
                </Typography>
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason for Cancellation (Optional)"
                placeholder="Please provide a reason for cancelling this service request..."
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                margin="normal"
                sx={{ mt: 1 }}
                helperText="This helps us improve our service"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={() => {
              setDeleteDialog({ open: false, request: null });
              setCancellationReason('');
            }}
            disabled={cancelLoading}
          >
            Keep Request
          </Button>
          <Button
            type="button"
            onClick={() => handleCancelRequest(deleteDialog.request)}
            color="error"
            variant="contained"
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Cancelling...' : 'Cancel Service Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {agreementDialog}
    </Container>
  );
};

export default ServiceRequestPage;